import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "cn";
import { createRegex } from "human-regex";
import type * as React from "react";

const generateColorFromCharacter = (char: string) => {
  const charCode = char?.toUpperCase()?.charCodeAt(0);
  const hue = (charCode - 65) * (360 / 26); // Spread colors evenly across the hue spectrum

  return `hsl(${hue}, 70%, 50%)`;
};

const getInitials = (name: string, singleChar?: boolean): string => {
  if (!name?.trim()) return "";

  const spaceRegex = createRegex().whitespace().toRegExp();
  const initials = name
    .split(spaceRegex)
    .map((word: string) => word?.charAt(0)?.toUpperCase())
    .join("");

  return singleChar ? initials?.charAt(0) : initials?.slice(0, 2);
};

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        /* sorted */
        "relative flex shrink-0 overflow-hidden",
        "size-8",
        "rounded-full",
        className,
      )}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("object-cover size-full", className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        /* sorted */
        "flex items-center justify-center",
        "size-full",
        "text-text-light",
        "bg-muted rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export {
  Avatar,
  AvatarFallback,
  AvatarImage,
  generateColorFromCharacter,
  getInitials,
};
